/**
 * Unified Compliance Reports Component
 * Handles all report views: list, templates, builder, viewer, editor
 * Loads templates dynamically from policy packs or fallback configs
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { ReportsList } from './ReportsList';
import { ReportViewer } from './ReportViewer';
import { ReportEditor } from './ReportEditor';
import { ReportBuilder } from './ReportBuilder';
import { ReportTemplateSelector } from './ReportTemplateSelector';
import { ReportBuilderForm } from './ReportBuilderForm';
import { useWorkspace } from '../../hooks/useWorkspace';
import { usePermissions } from '../../hooks/usePermissions';
import {
  canCreateProposal,
  canEditProposal,
  canArchiveProposal,
} from '../../utils/rbacResourceAccess';
import { useReportJobSubmit } from '../../hooks/useReportJob';
import { useReportTemplates } from '../../hooks/useReportTemplates';
import { ReportTemplate } from '../../types/reports';
import { FileText, ArrowLeft, Plus } from 'lucide-react';

type View = 'list' | 'templates' | 'builder' | 'viewer' | 'editor';

interface ComplianceReportsProps {
  fallbackTemplates?: ReportTemplate[];
  title?: string;
  description?: string;
  onReportTypeMapper?: (frontendType: string) => string; // Maps frontend type to backend enum
  initialReportId?: string; // Optional report ID to open in viewer on mount
  hideHeader?: boolean; // Hide header when viewing a specific report
  onReportSelect?: (reportId: string | null) => void; // Called when user opens (reportId) or closes (null) report viewer – e.g. to sync URL and hide filters
  reportsQueryKeySuffix?: string; // Optional suffix for reports query key (e.g., 'rfp_proposals')
  listReportsOptions?: { exclude_report_type?: string; report_type?: string; status?: string; source_type?: string }; // Options to pass to listReports
  /** Override label for the primary "new" button (e.g. "Find Opportunity to Create Proposal") */
  newProposalLabel?: string;
  /** When set, primary button and list empty-state call this instead of opening template selector (e.g. navigate to Government Data) */
  onNewProposalClick?: () => void;
  /** When true, show Resume button for checkpointed report jobs (admin/tech only) */
  canResumeCheckpointed?: boolean;
  /**
   * When set (e.g. Keystone RFP proposal list), gates create/edit/delete UI by permission strings.
   * Omit for generic report UIs (e.g. other products) so existing behavior stays unchanged.
   */
  proposalWriteAccess?: {
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
  };
}

interface State {
  view: View;
  selectedReportId?: string;
  selectedTemplate?: ReportTemplate;
}

export function ComplianceReports({
  fallbackTemplates = [],
  title = 'Reports',
  description = 'View and manage all reports across projects',
  onReportTypeMapper,
  initialReportId,
  hideHeader = false,
  onReportSelect,
  reportsQueryKeySuffix,
  listReportsOptions,
  newProposalLabel,
  onNewProposalClick,
  canResumeCheckpointed = false,
  proposalWriteAccess: proposalWriteAccessProp,
}: ComplianceReportsProps) {
  const { projectId } = useWorkspace();
  const { hasAnyPermission } = usePermissions();

  const isRfpProposalsContext = reportsQueryKeySuffix === 'rfp_proposals' || title === 'RFP Proposals';
  const proposalWriteAccess =
    proposalWriteAccessProp ??
    (isRfpProposalsContext
      ? {
          canCreate: canCreateProposal(hasAnyPermission),
          canEdit: canEditProposal(hasAnyPermission),
          canDelete: canArchiveProposal(hasAnyPermission),
        }
      : undefined);
  const [state, setState] = useState<State>({
    view: initialReportId ? 'viewer' : 'list',
    selectedReportId: initialReportId,
  });
  const submitMutation = useReportJobSubmit();

  // UX polish: singular title when viewing one proposal
  const displayTitle =
    state.view === 'viewer' && title === 'RFP Proposals'
      ? 'RFP Proposal'
      : title;

  // Handle initialReportId prop changes
  useEffect(() => {
    if (initialReportId && initialReportId !== state.selectedReportId) {
      setState({ view: 'viewer', selectedReportId: initialReportId });
      onReportSelect?.(initialReportId);
    }
  }, [initialReportId]);

  // Load templates from policy pack or fallback
  const { templates, reportTypes, isLoading: templatesLoading } = useReportTemplates({
    fallbackTemplates,
  });

  const handleSelectTemplate = (template: ReportTemplate) => {
    setState({ view: 'builder', selectedTemplate: template });
  };

  const handleCustomReport = () => {
    setState({ view: 'builder', selectedTemplate: undefined });
  };

  const handleViewReport = (reportId: string) => {
    setState({
      view: 'viewer',
      selectedReportId: reportId
    });
    pushProposalUrl(reportId);
    onReportSelect?.(reportId);
  };

  const handleEditReport = () => {
    if (state.selectedReportId) {
      setState({ view: 'editor', selectedReportId: state.selectedReportId });
    }
  };

  const handleGenerateReport = async (clientName: string, questions: any[]) => {
    if (!projectId || !state.selectedTemplate) return;

    try {
      console.log('[ComplianceReports] Generating report:', {
        project_id: projectId,
        report_type: state.selectedTemplate.type,
        client_name: clientName,
        questions_count: questions.length,
      });

      // Map frontend type to backend enum if mapper provided
      const backendType = onReportTypeMapper
        ? onReportTypeMapper(state.selectedTemplate.type)
        : 'compliance'; // Default to compliance

      const jobStatus = await submitMutation.mutateAsync({
        project_id: projectId,
        report_type: backendType as any,
        client_name: clientName,
        questions: questions,
        enable_cache: true,
        metadata: {
          frontend_type: state.selectedTemplate.type,
          template_id: state.selectedTemplate.id,
          template_name: state.selectedTemplate.name,
          regulatory_context: state.selectedTemplate.regulatoryContext,
        },
      });

      console.log('[ComplianceReports] Report job submitted:', jobStatus);

      // Navigate to list view to see the new report
      setState({ view: 'list' });
    } catch (error: any) {
      console.error('[ComplianceReports] Failed to generate report:', error);
      const validationErrors = error.response?.data?.detail;
      const errorMessage = validationErrors
        ? `Validation failed: ${JSON.stringify(validationErrors, null, 2)}`
        : error.message;

      alert(`Failed to generate report:\n${errorMessage}`);
    }
  };

  const isProposalMgmtRoute = () => {
    if (typeof window === 'undefined') return false;
    return window.location.pathname.includes('proposals-management');
  };

  const pushProposalUrl = (reportId: string) => {
    if (!isRfpProposalsContext || !isProposalMgmtRoute()) return;
    const url = new URL(window.location.href);
    url.searchParams.set('tab', 'list');
    url.searchParams.set('proposal_id', reportId);
    url.searchParams.delete('reportId');
    window.history.pushState({}, '', url.toString());
    window.dispatchEvent(new Event('navigate'));
  };

  const clearUrlParams = () => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (url.searchParams.has('proposal_id') || url.searchParams.has('reportId')) {
        url.searchParams.delete('proposal_id');
        url.searchParams.delete('reportId');
        window.history.replaceState({}, '', url.toString());
        window.dispatchEvent(new Event('navigate'));
      }
    }
  };

  const handleBack = () => {
    if (state.view === 'builder' || state.view === 'templates') {
      setState({ view: 'list' });
    } else if (state.view === 'viewer' || state.view === 'editor') {
      clearUrlParams();
      setState({ view: 'list' });
      onReportSelect?.(null); // Notify parent so e.g. filter panel can show again
    }
  };

  if (!projectId) {
    return (
      <Card>
        <p className="text-center py-12 text-theme-text-muted">
          Select a project to access reports
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - Hide when hideHeader is true and viewing a report */}
      {!(hideHeader && (state.view === 'viewer' || state.view === 'editor')) && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {state.view !== 'list' && (
              <button
                onClick={handleBack}
                className="p-2 hover:bg-theme-surface-hover rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-theme-text-secondary" />
              </button>
            )}
            <div>
              <h1 className="text-3xl font-bold text-theme-text flex items-center gap-3">
                <FileText className="h-8 w-8 text-blue-600" />
                {displayTitle}
              </h1>
              <p className="text-theme-text-secondary mt-1">
                {state.view === 'list' && description}
                {state.view === 'templates' && 'Choose a report template'}
                {state.view === 'builder' && 'Configure and generate report'}
                {state.view === 'viewer' && 'View report details'}
                {state.view === 'editor' && 'Edit report content'}
              </p>
            </div>
          </div>

          {state.view === 'list' && (proposalWriteAccess?.canCreate !== false) && (
            <Button
              onClick={onNewProposalClick ?? (() => setState({ view: 'templates' }))}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-8" />
              {newProposalLabel ?? 'New Proposal'}
            </Button>
          )}
        </div>
      )}

      {/* Content based on view */}
      {state.view === 'list' && (
        <ReportsList
          onViewReport={handleViewReport}
          queryKeySuffix={reportsQueryKeySuffix}
          listReportsOptions={listReportsOptions}
          onCreateNew={onNewProposalClick ?? (() => setState({ view: 'templates' }))}
          canResumeCheckpointed={canResumeCheckpointed}
          proposalWriteAccess={proposalWriteAccess}
        />
      )}

      {state.view === 'templates' && (
        <ReportTemplateSelector
          templates={templates}
          onSelectTemplate={handleSelectTemplate}
          onCustomReport={handleCustomReport}
          title="Choose a Report Template"
          description="Select a pre-configured template or build a custom report from scratch"
        />
      )}

      {state.view === 'builder' && (
        <>
          {!state.selectedTemplate ? (
            <ReportBuilder
              availableTypes={reportTypes}
              onComplete={(reportId) => {
                console.log('[ComplianceReports] Report complete:', reportId);
                if (reportId) {
                  setState({ view: 'viewer', selectedReportId: reportId });
                  pushProposalUrl(reportId);
                  onReportSelect?.(reportId);
                } else {
                  setState({ view: 'list' });
                }
              }}
            />
          ) : (
            <ReportBuilderForm
              template={state.selectedTemplate}
              onGenerate={handleGenerateReport}
              onCancel={handleBack}
              isLoading={submitMutation.isPending}
            />
          )}
        </>
      )}

      {state.view === 'viewer' && state.selectedReportId && (
        <ReportViewer
          reportId={state.selectedReportId}
          onEdit={proposalWriteAccess?.canEdit === false ? undefined : handleEditReport}
          proposalWriteAccess={proposalWriteAccess}
          onClose={() => {
            clearUrlParams();
            setState({ view: 'list' });
            onReportSelect?.(null);
          }}
        />
      )}

      {state.view === 'editor' && state.selectedReportId && proposalWriteAccess?.canEdit !== false && (
        <ReportEditor
          reportId={state.selectedReportId}
          onSave={() => setState({ view: 'viewer', selectedReportId: state.selectedReportId })}
          onCancel={() => setState({ view: 'viewer', selectedReportId: state.selectedReportId })}
        />
      )}
    </div>
  );
}

